export * from '@shared/config';
import { CDN_URLS, OCI_FOLDERS, APP_CONFIG } from '@shared/config';
export { CDN_URLS, OCI_FOLDERS, APP_CONFIG };
import { API_KEYS, updateApiKeys } from '@shared/config';

// UIX specific configurations
import { FIREBASE_CONFIG as SHARED_FIREBASE_CONFIG } from '@shared/config';
export const UIX_CONFIG = {
  PORT: process.env.PORT || 8080,
  ADMIN_PASSWORD: APP_CONFIG.ADMIN_PASSWORD,
  GA_TRACKING_ID: process.env.GA_TRACKING_ID || 'G-N3R470CBGW',
  SERVICE_NAME: 'constantina-styling-frontend',
  SERVICE_VERSION: '1.0.8',
  ENABLE_MEMBERSHIPS: false,
};

// UIX/config.ts - Check for localStorage overrides to allow dynamic switching in Admin Console
const getBackendOverride = () => {
    if (typeof window === 'undefined') return {};
    return {
        useExternal: localStorage.getItem('VITE_USE_EXTERNAL_BACKEND'),
        url: localStorage.getItem('VITE_BACKEND_URL'),
        useFirebase: localStorage.getItem('VITE_USE_FIREBASE_CONFIG_STORAGE'),
    };
};

const overrides = getBackendOverride();

export const BACKEND_CONFIG = {
    /** Set VITE_USE_EXTERNAL_BACKEND=true in .env to use the real backend API instead of localStorage */
    USE_EXTERNAL_BACKEND: overrides.useExternal !== null
        ? overrides.useExternal === 'true'
        : typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_USE_EXTERNAL_BACKEND === 'true',
    /** Backend API base URL — override with VITE_BACKEND_URL env var */
    BACKEND_URL: overrides.url || (typeof import.meta !== 'undefined' ? ((import.meta as any).env?.VITE_BACKEND_URL || 'http://localhost:3000') : 'http://localhost:3000'),
};

export const FIREBASE_CONFIG = {
    ...SHARED_FIREBASE_CONFIG,
    // UIX default: true — use Firestore as config source.
    // Override via localStorage key 'VITE_USE_FIREBASE_CONFIG_STORAGE' (Admin Console)
    // or via the VITE_USE_FIREBASE_CONFIG_STORAGE env var.
    USE_FIREBASE_CONFIG_STORAGE: overrides.useFirebase !== null
        ? overrides.useFirebase === 'true'
        : typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_USE_FIREBASE_CONFIG_STORAGE !== undefined
            ? (import.meta as any).env.VITE_USE_FIREBASE_CONFIG_STORAGE === 'true'
            : true, // <-- UIX default: enabled
};

/**
 * Validates that the Firebase configuration contains real values.
 * Logs warnings (not errors) so the app still starts in degraded mode.
 */
export function validateFirebaseConfig(): boolean {
    const PLACEHOLDER_KEYS = ['AIzaSyBxQu9rRWGzDAwhbIBryY7qE3aM5oCk_4E', 'AIzaSyD5c9VN2xuwEggRAWgduWU70umrWHWRecQ', ''];
    const cfg = FIREBASE_CONFIG;
    const issues: string[] = [];

    if (!cfg.PROJECT_ID || cfg.PROJECT_ID === 'codina-outfit')
        issues.push('PROJECT_ID appears to be a placeholder or missing');

    if (!cfg.CLIENT_EMAIL || cfg.CLIENT_EMAIL.includes('konstantina@'))
        issues.push('CLIENT_EMAIL is not a Firebase service-account email (should end in @...gserviceaccount.com)');

    if (!cfg.PRIVATE_KEY || PLACEHOLDER_KEYS.some(p => cfg.PRIVATE_KEY.startsWith(p)))
        issues.push('PRIVATE_KEY is a placeholder API key — set FIREBASE_PRIVATE_KEY env var with the real private key');

    if (!cfg.STORAGE_BUCKET || !cfg.STORAGE_BUCKET.includes('.firebasestorage'))
        issues.push('STORAGE_BUCKET does not look like a valid Firebase storage bucket');

    if (issues.length > 0) {
        console.warn('[Firebase Config] ⚠️  Validation issues detected:');
        issues.forEach(i => console.warn(`  • ${i}`));
        console.warn('[Firebase Config] USE_FIREBASE_CONFIG_STORAGE =', cfg.USE_FIREBASE_CONFIG_STORAGE);
        return false;
    }

    console.info('[Firebase Config] ✅ Config validated — USE_FIREBASE_CONFIG_STORAGE =', cfg.USE_FIREBASE_CONFIG_STORAGE);
    return true;
}

// Run validation at module load time (non-blocking)
if (typeof window !== 'undefined') {
    validateFirebaseConfig();
}

export const GCP_CONFIG = {
  projectId: 'codina-styling-assistant',
  bucketName: 'codina-outfit-assets',
};

// Re-export pricing specifically if needed by local logic
export { PRICING, formatPrice, getServicePrice } from './config/pricing';

// Detect and apply overrides directly to the shared API_KEYS singleton for UIX
const geminiOverride = (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_GEMINI_API_KEY) || process.env.VITE_GEMINI_API_KEY  || 'AIzaSyAwDq6qyKAeLRu-IByzvWmW7LmlEj_PTR8';
const googleOverride = (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_GOOGLE_API_KEY) || process.env.VITE_GOOGLE_API_KEY;

if (geminiOverride || googleOverride) {
  updateApiKeys({
    ...(geminiOverride ? { gemini: geminiOverride } : {}),
    ...(googleOverride ? { googleApiKey: googleOverride } : {}),
  });
}

// Extend shared API_KEYS with UIX-specific keys (Google Workspace)
export const apiKeys = {
  ...API_KEYS,
  googleWorkspace: {
    clientId: process.env.GOOGLE_CLIENT_ID || API_KEYS.googleWorkspace.clientId,
    clientSecret: process.env.GOOGLE_CLIENT_SECRET || API_KEYS.googleWorkspace.clientSecret,
    refreshToken: process.env.GOOGLE_REFRESH_TOKEN || API_KEYS.googleWorkspace.refreshToken,
  },
  oci: {
    tenancyId: process.env.OCI_TENANCY_ID || 'ocid1.tenancy.oc1..aaaaaa_placeholder',
    userId: process.env.OCI_USER_ID || 'ocid1.user.oc1..aaaaaa_placeholder',
    fingerprint: process.env.OCI_FINGERPRINT || '00:00:00:00:00:00:00:00:00:00:00:00:00:00:00:00',
    privateKey: process.env.OCI_PRIVATE_KEY || '-----BEGIN RSA PRIVATE KEY-----\n...\n-----END RSA PRIVATE KEY-----',
    region: process.env.OCI_REGION || 'eu-zurich-1'
  }
};

export const socialIntegrations = {
  instagram: {
    appId: process.env.VITE_INSTAGRAM_APP_ID || 'PLACEHOLDER_INSTAGRAM_APP_ID',
    accessToken: process.env.VITE_INSTAGRAM_ACCESS_TOKEN || 'PLACEHOLDER_INSTAGRAM_ACCESS_TOKEN',
  },
  twitter: {
    apiKey: process.env.VITE_TWITTER_API_KEY || 'PLACEHOLDER_TWITTER_API_KEY',
  },
  youtube: {
    apiKey: process.env.VITE_YOUTUBE_API_KEY || 'PLACEHOLDER_YOUTUBE_API_KEY',
    playlistId: process.env.VITE_YOUTUBE_PLAYLIST_ID || 'PLACEHOLDER_YOUTUBE_PLAYLIST_ID',
  },
  facebook: {
    appId: process.env.VITE_FACEBOOK_APP_ID || 'PLACEHOLDER_FACEBOOK_APP_ID',
  },
};

export const OCI_LOGGING_CONFIG = {
  ENDPOINT: 'https://ingestion.loganalytics.eu-zurich-1.oci.oraclecloud.com',
  LOG_GROUP_ID: 'ocid1.loggroup.oc1..aaaaaa_placeholder',
  NAMESPACE: 'placeholder_namespace',
  TENANCY_ID: apiKeys.oci.tenancyId,
  USER_ID: apiKeys.oci.userId,
  KEY_FINGERPRINT: apiKeys.oci.fingerprint,
  PRIVATE_KEY: apiKeys.oci.privateKey,
  REGION: apiKeys.oci.region
};

